import { keys, set } from "lodash";

import { EAR_MOLD_SUBTYPE } from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE
} from "constants/sales";
import { MasterProduct } from "services/types/master-products.type";
import { ProductInput } from "services/types/products.type";

import { ProductFormValues } from "./product-form.component";

/**
 * Contract for cpt_code vs cpt_codes:
 *   - `cpt_code` is the PRIMARY CPT code (FK on the BE).
 *   - `cpt_codes` is the list of ADDITIONAL codes only — it does NOT contain
 *     the primary. The UI's "Additional CPT or HCPCS Codes" multi-select reflects this
 *     and the picker actively excludes the primary id (see `excludeIds`).
 *
 * Server-side, when both are provided, the BE persists both as-is (the FK
 * sync helper short-circuits when `cpt_code` is explicit). Consumers that need
 * "all codes for this product" must union the primary + the M2M list.
 */
export const formatProductFormValues = (
  values: ProductFormValues,
  multipleCptCodesFeature?: boolean,
  productCategoriesFeature?: boolean
): ProductInput => {
  const {
    taxable,
    type,
    name,
    price,
    supplier_price,
    component_price,
    max_discount,
    loss_and_deductible_amount,
    is_active,
    managed_stock,
    master_product,
    cpt_code,
    cpt_codes,
    warranty_months,
    loss_damage_warranty_months,
    manufacturer_loss_damage_cost,
    auto_delivery,
    product_account,
    starred,
    repair_eligible,
    tier,
    subtype,
    category
  } = values;

  const isEarmold =
    Number(type) === ACCESSORY_PRODUCT_TYPE &&
    Number(subtype) === EAR_MOLD_SUBTYPE;

  const result: ProductInput = {
    name,
    price,
    supplier_price,
    component_price,
    max_discount,
    loss_and_deductible_amount: loss_and_deductible_amount ?? "",
    master_product: master_product?.id ?? undefined,
    is_active,
    managed_stock,
    cpt_code: cpt_code?.id ?? null,
    ...(multipleCptCodesFeature
      ? { cpt_codes: cpt_codes.map((c) => c.id) }
      : {}),
    taxable,
    auto_delivery,
    starred,
    repair_eligible,
    product_account: product_account?.id,
    tier: tier || null,
    // Sent only with the flag on: the serializer 400s an unknown key, so a
    // flag-off client must stay byte-identical against an older backend.
    ...(productCategoriesFeature
      ? { category: category === "" || category == null ? null : category }
      : {})
  };

  if (Number(type) === HEARING_AID_PRODUCT_TYPE) {
    updateHearingAidSpecification(result, {
      warranty_months,
      loss_damage_warranty_months,
      manufacturer_loss_damage_cost
    });
  }

  if (isEarmold) {
    updateHearingAidSpecification(result, {
      warranty_months,
      loss_damage_warranty_months
    });
  }

  return result;
};

const updateHearingAidSpecification = (
  input: ProductInput,
  specification: Record<string, string>
) => {
  keys(specification).forEach((fieldName) => {
    const value = specification[fieldName];
    if (String(value).length) {
      set(input, `specification.${fieldName}`, value);
    }
  });
};

export type ClassificationChange = { type: number; subtype: number | null };

/**
 * The classification change this form would make to the entry it is ON, or `null` (SN-19689).
 *
 * The distinction the whole screen turns on: pointing the product at a DIFFERENT entry is a
 * relink, which the ordinary save writes like any other field and which must never trigger a
 * re-classification. Only a changed Type or Sub Type on the SAME entry is one.
 *
 * Entry identity is what makes that unambiguous. An earlier version compared only Type and Sub
 * Type against the original entry, so choosing a different entry — whose classification of
 * course differs — read as "the user re-classified this one", and offered to duplicate an entry
 * they had already navigated away from.
 */
export const getClassificationChange = (
  values: Pick<ProductFormValues, "type" | "subtype" | "master_product">,
  masterProduct: MasterProduct | null | undefined
): ClassificationChange | null => {
  if (!masterProduct) return null;

  // A different entry (or none yet) is a relink, not a re-classification.
  if (values.master_product?.id !== masterProduct.id) return null;

  const type = values.type === "" ? null : Number(values.type);
  const subtype =
    values.subtype === "" ||
    values.subtype === null ||
    values.subtype === undefined
      ? null
      : Number(values.subtype);

  if (type === null || Number.isNaN(type)) return null;
  if (type === masterProduct.type && subtype === masterProduct.subtype)
    return null;

  return { type, subtype };
};
